import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useApolloClient, useMutation } from "@apollo/react-hooks";
import * as Yup from "yup";
import { FormErrors, Modal } from "@squirrellearningltd/go-teacher-components";
import { GET_FRANCHISE_INVITE_SCHOOLS, INVITE_SCHOOL_ADMINISTRATOR, INVITE_FRANCHISE_ADMIN } from "../services/graphql";
import styles from "../Franchises.module.scss";

const InviteSchoolAdministrator = ({ school, franchiseId, franchiseAdmin = false, closeModal, onInvited }) => {
  const [formErrors, setFormErrors] = useState([]);
  const saving = useRef(false);
  const client = useApolloClient();
  const [schools, setSchools] = useState([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState(school ? school.id : "");
  const [schoolsLoading, setSchoolsLoading] = useState(!school && !franchiseAdmin);
  const [schoolsError, setSchoolsError] = useState("");

  useEffect(() => {
    if (franchiseAdmin || school || !franchiseId) return;
    let cancelled = false;
    const loadSchools = (page, previousSchools) => client.query({
      query: GET_FRANCHISE_INVITE_SCHOOLS,
      variables: { franchiseId, page, perPage: 100 },
      fetchPolicy: "no-cache"
    }).then(({ data }) => {
      if (cancelled) return;
      const directory = data && data.franchiseSchools;
      if (!directory) throw new Error("Unable to load schools.");
      const allSchools = previousSchools.concat(directory.schools || []);
      if (page < directory.totalPages) return loadSchools(page + 1, allSchools);
      setSchools(allSchools);
      setSchoolsLoading(false);
    });
    loadSchools(1, []).catch(() => {
      if (cancelled) return;
      setSchoolsLoading(false);
      setSchoolsError("Unable to load schools. Please close the modal and try again.");
    });
    return () => { cancelled = true; };
  }, [client, franchiseId, school, franchiseAdmin]);
  const [inviteAdministrator, { loading }] = useMutation(franchiseAdmin ? INVITE_FRANCHISE_ADMIN : INVITE_SCHOOL_ADMINISTRATOR);
  const schema = Yup.object().shape({
    name: Yup.string().trim().required("Please enter a full name"),
    email: Yup.string().trim().email("Please enter a valid email address").required("Please enter an email address")
  });
  const { register, handleSubmit, errors, watch } = useForm({
    validationSchema: schema,
    mode: "onChange",
    defaultValues: { name: "", email: "" }
  });
  const canSend = (franchiseAdmin ? Boolean(franchiseId) : Boolean(selectedSchoolId) && !schoolsLoading && !schoolsError) && schema.isValidSync(watch());
  const close = () => {
    if (!saving.current) closeModal();
  };
  const onSubmit = values => {
    if (saving.current || !canSend) return;
    saving.current = true;
    setFormErrors([]);
    inviteAdministrator({ variables: {
      ...(franchiseAdmin ? { franchiseId: decodeURIComponent(franchiseId) } : { schoolId: selectedSchoolId, sendAdminInvitation: true }),
      name: values.name.trim(),
      email: values.email.trim()
    } }).then(({ data }) => {
      const result = data && (franchiseAdmin ? data.inviteFranchiseAdmin : data.inviteSchoolAdministrator);
      if (result && result.errors && result.errors.length) {
        saving.current = false;
        setFormErrors(result.errors);
        return;
      }
      const invitedAccount = result && (franchiseAdmin ? result.membership : result.admin);
      if (!invitedAccount || !invitedAccount.id) {
        saving.current = false;
        setFormErrors(["Unable to invite the administrator. Please try again."]);
        return;
      }
      onInvited();
    }).catch(error => {
      saving.current = false;
      setFormErrors([error.message]);
    });
  };

  return (
    <Modal closeModal={close}>
      <div className={styles.inviteAdminModal} role="dialog" aria-modal="true" aria-labelledby="inviteSchoolAdminTitle">
        <h2 id="inviteSchoolAdminTitle">{franchiseAdmin ? "Invite franchise administrator" : "Invite school administrator"}</h2>
        <p className={styles.statDescription}>{franchiseAdmin ? "Send a secure invitation to set up a franchise administrator account." : "Choose the school and send a secure account setup invitation."}</p>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          {!franchiseAdmin && <div className={styles.formGroup}>
            <label htmlFor="inviteSchool" className="basic-form__text-label">School</label>
            <select id="inviteSchool" className="basic-form__text-box" value={selectedSchoolId} onChange={event => setSelectedSchoolId(event.target.value)} disabled={Boolean(school) || schoolsLoading || loading} aria-required="true">
              {school ? <option value={school.id}>{school.name}</option> : <>
                <option value="">{schoolsLoading ? "Loading schools..." : "Select a school"}</option>
                {schools.map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
              </>}
            </select>
            {schoolsError && <p role="alert">{schoolsError}</p>}
            {!school && !schoolsLoading && !schoolsError && schools.length === 0 && <p>No schools found in this franchise.</p>}
          </div>}
          <div className={styles.schoolFormTwoColumns}>
            <div className={styles.formGroup}>
              <label htmlFor="inviteAdminName" className="basic-form__text-label">Full name</label>
              <input id="inviteAdminName" name="name" type="text" className="basic-form__text-box" ref={register} aria-required="true" disabled={loading} />
              {errors.name && <p className="basic-form__hint u-m-top-0">{errors.name.message}</p>}
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="inviteAdminEmail" className="basic-form__text-label">Email address</label>
              <input id="inviteAdminEmail" name="email" type="email" className="basic-form__text-box" ref={register} aria-required="true" disabled={loading} />
              {errors.email && <p className="basic-form__hint u-m-top-0">{errors.email.message}</p>}
            </div>
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="inviteAdminRole" className="basic-form__text-label">Role</label>
            <select id="inviteAdminRole" className="basic-form__text-box" value="1" disabled>
              <option value="1">{franchiseAdmin ? "Franchise administrator" : "School administrator"}</option>
            </select>
          </div>
          {formErrors.length > 0 && <FormErrors errors={formErrors} />}
          <div className={styles.formActions}>
            <button type="button" className={styles.button} onClick={close} disabled={loading}>Cancel</button>
            <button type="submit" className={styles.primaryButton} disabled={!canSend || loading}>{loading ? "Sending..." : "Send invite"}</button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default InviteSchoolAdministrator;
